import groupBy from 'lodash/groupBy';
import upperFirst from 'lodash/upperFirst';

import { formatMonthList, formatPeriod, formatPeriods } from './formatMonthList';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import { StatementFiling } from 'services/bookkeeping/queries/useStatementUploadStatuses';

/** Resolves a document to the name its file was uploaded under, where the member's list has it. */
export type FileNameOf = (documentUuid: string) => string | undefined;

const ACCOUNT_LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/** An account as "··4035" — last four, the form the server's refusals (shown verbatim) use. */
export function accountMaskLabel(mask: string | null | undefined): string {
    const trimmed = mask?.trim();
    return trimmed ? `··${trimmed.slice(-4)}` : 'another account';
}

function isHeld(filing: StatementFiling): boolean {
    return filing.outcome === 'held_by_bank' || filing.outcome === 'held_by_upload';
}

/**
 * "··4035 July 2026 was already sent by your bank", or "…is already covered by june.pdf". Always
 * with the year: a window can span a year boundary.
 */
function heldSentence(filing: StatementFiling, fileNameOf: FileNameOf): string {
    const statement = upperFirst(`${accountMaskLabel(filing.account_mask)} ${formatPeriod(filing)}`);
    if (filing.outcome === 'held_by_bank') {
        return `${statement} was already sent by your bank.`;
    }
    // A holder the member's list can't name is still a real holder.
    const holder = filing.held_by_member_document_uuid ? fileNameOf(filing.held_by_member_document_uuid) : undefined;
    return `${statement} is already covered by ${holder ?? 'another file'}.`;
}

/** Filings on accounts other than `homeAccountId`, the card the file was uploaded under. */
function elsewhere(filings: StatementFiling[], homeAccountId: string | null): StatementFiling[] {
    return filings.filter((filing) => filing.bank_account_id !== homeAccountId);
}

/** The `filed` entries on accounts other than `excludedAccountId`, grouped per account in file order. */
function filedByOtherAccount(filings: StatementFiling[], excludedAccountId: string | null): StatementFiling[][] {
    const filed = filings.filter(
        (filing) => filing.outcome === 'filed' && filing.bank_account_id !== excludedAccountId
    );
    return Object.values(groupBy(filed, 'bank_account_id'));
}

/** The distinct accounts a set of filings names, in the order the file prints them. */
function accountLabels(filings: StatementFiling[]): string[] {
    return [...new Set(filings.map((filing) => accountMaskLabel(filing.account_mask)))];
}

/** Whether the file holds a statement on an account other than its home card; the server 409s a second filing. */
export function isFiledOnAnotherAccount(filings: StatementFiling[], homeAccountId: string | null): boolean {
    return elsewhere(filings, homeAccountId).some((filing) => filing.outcome === 'filed');
}

/**
 * Lines under a file about its statements on other accounts: "Also covers ··4035 (Jun, Jul 2026)"
 * per account, then each held one's holder. `includeHomeHeld` adds held home-card statements too,
 * for when that isn't already the headline. `not_filed` says nothing.
 */
export function filingNotes(
    filings: StatementFiling[],
    homeAccountId: string | null,
    fileNameOf: FileNameOf,
    { includeHomeHeld }: { includeHomeHeld: boolean }
): string[] {
    const others = elsewhere(filings, homeAccountId);

    const alsoCovers = filedByOtherAccount(filings, homeAccountId).map(
        (group) => `Also covers ${accountMaskLabel(group[0].account_mask)} (${formatMonthList(group)})`
    );

    const held = (includeHomeHeld ? filings : others).filter(isHeld);
    return [...alsoCovers, ...held.map((filing) => heldSentence(filing, fileNameOf))];
}

/**
 * Why a file holds no month on its home card, naming the holder. Undefined when nothing holds it,
 * so the caller keeps its generic copy.
 */
export function heldMessage(
    filings: StatementFiling[],
    homeAccountId: string | null,
    fileNameOf: FileNameOf
): string | undefined {
    const home = filings.filter((filing) => filing.bank_account_id === homeAccountId && isHeld(filing));
    if (home.length === 0) {
        return undefined;
    }
    return home.map((filing) => heldSentence(filing, fileNameOf)).join(' ');
}

/**
 * A wrong-account refusal that also names where the file went, so it isn't uploaded again there.
 * Undefined when it names no other account of the member's.
 */
export function wrongAccountMessage(
    filings: StatementFiling[],
    homeAccountId: string | null,
    fileNameOf: FileNameOf
): string | undefined {
    const others = elsewhere(filings, homeAccountId);
    const filedOn = accountLabels(others.filter((filing) => filing.outcome === 'filed'));
    const held = others.filter(isHeld).map((filing) => heldSentence(filing, fileNameOf));
    if (filedOn.length === 0 && held.length === 0) {
        return undefined;
    }
    const opening =
        filedOn.length > 0
            ? `This statement doesn't match this account, so we added it to ${ACCOUNT_LIST.format(filedOn)}.`
            : "This statement doesn't match this account.";
    return [opening, ...held].join(' ');
}

/** The card whose window this is, as "··5555" — or "this account", never "another", without a mask. */
export function thisAccountLabel(mask: string): string {
    return mask.trim() ? accountMaskLabel(mask) : 'this account';
}

interface RemovalNoticeArgs {
    readonly removedFrom: { bank_account_id: string | null; account_mask: string };
    /** The month taken off the card. Absent when everything the file held there went. */
    readonly period?: StatementPeriod;
    readonly fileName?: string;
    /** The server deleted the file, because it held nothing anywhere afterwards. */
    readonly isDeleted: boolean;
    /** The file's filings after the removal. */
    readonly filings: StatementFiling[];
}

/**
 * What a removal did, said after the fact — "Removed March 2026 from ··5555. Still covering ··7777
 * July 2026.", or "…and deleted june.pdf." — because the row vanishing reads the same either way.
 */
export function removalNotice({ removedFrom, period, fileName, isDeleted, filings }: RemovalNoticeArgs): string {
    const card = thisAccountLabel(removedFrom.account_mask);
    const file = fileName ?? 'the file';
    if (isDeleted) {
        return period ? `Removed ${formatPeriod(period)} from ${card} and deleted ${file}.` : `Deleted ${file}.`;
    }
    const removed = `Removed ${period ? formatPeriod(period) : file} from ${card}.`;
    const stillCovering = filedByOtherAccount(filings, removedFrom.bank_account_id).map(
        (group) => `${accountMaskLabel(group[0].account_mask)} ${formatPeriods(group)}`
    );
    return stillCovering.length > 0 ? `${removed} Still covering ${ACCOUNT_LIST.format(stillCovering)}.` : removed;
}
